import React, {
  useEffect, useRef, useState, memo,
} from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/swiper-bundle.min.css';
// import ProductsSliderModal from "./ProductsSliderModal";
// import ProductsSliderModalMobile from './ProductsSliderModalMobile';
// import ModalFullScreen from './ModalFullScreen';
import Flickity from 'react-flickity-component';
import { BsChevronRight, BsChevronLeft } from 'react-icons/bs';
import useBreakpoint from '@/hooks/useBreakpoint';
import useMedia from '../../../../hooks/useMedia';

const ProductsSliderModalMobileAsync = React.lazy(() => import('./ProductsSliderModalMobile'));

const ModalFullScreenAsync = React.lazy(() => import('./ModalFullScreen'));

SwiperCore.use([Navigation]);

const DESKTOP_IMAGE_MEDIA = {
  900: 1024,
  1200: 1300,
  1600: 1500,
};

const matchImageToMedia = (media, image) => (
  Object.keys(media).reduce((acc, key, index) => (
    (`${acc}${index > 0 ? ',' : ''}${window.resizeImage(image, media[key])} ${key}w`)
  ), '')
);

const flickityOptions = {
  freeScroll: true,
  wrapAround: true,
  prevNextButtons: false,
  pageDots: false,
  cellAlign: 'left',
  percentPosition: true,
  draggable: true,
};

function SliderSection({ settings }) {
  const swiperRef = useRef(null);
  const swiperOutterRef = useRef(null);
  const requestId = useRef(null);
  const flickityRef = useRef(null);
  const isMouseMoved = useRef(false);
  const lastPos = useRef(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStlProductIdx, setCurrentStlProductIdx] = useState(0);
  const [currentProductSlideIdx, setCurrentProductSlideIdx] = useState(0);
  const [activeHotspot, setActiveHotspot] = useState(0);

  const [breakpoint, breakpoints] = useBreakpoint();
  const { isDesktop } = useMedia(['screen and (min-width: 1009px)', 'isDesktop']);

  const openModal = (stl_product = currentStlProductIdx) => {
    setIsModalOpen(true);
    setCurrentStlProductIdx(stl_product);

    if (!isDesktop) return;

    lastPos.current = flickityRef.current?.flkty?.x;
  };

  const closeModal = () => {
    setIsModalOpen(false);
    if (isDesktop) play();
  };

  function play() {
    if (!flickityRef.current?.flkty) return;
    // Set the decrement of position x
    flickityRef.current.flkty.x -= 2.25 - (2 * settings.main_loop_animation_speed / 100);

    // Settle position into the slider
    flickityRef.current.flkty.settle(flickityRef.current.flkty.x);

    // Set the requestId to the local variable
    requestId.current = window.requestAnimationFrame(play);
  }

  function pause() {
    if (requestId.current) {
      // Cancel the animation
      window.cancelAnimationFrame(requestId.current);

      // Reset the requestId for the next animation.
      requestId.current = undefined;
    }
  }

  useEffect(() => {
    if (flickityRef.current?.flkty) {
      if (isDesktop) {
        flickityRef.current.flkty.on('dragMove', () => {
          isMouseMoved.current = true;
        });
        flickityRef.current.flkty.on('ready', () => {
          flickityRef.current.flkty.x = lastPos.current;
          flickityRef.current.flkty.settle(lastPos.current);
        });
        play();
      } else {
        pause();
      }
    }
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop && swiperRef.current?.swiper) {
      swiperRef.current.swiper.autoplay?.[
        isModalOpen ? 'stop' : 'start'
      ]();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (swiperRef.current && swiperOutterRef.current) {
      if (isModalOpen) {
        document.documentElement.style.overflowY = 'hidden';
        if (!isDesktop) {
          const offsetTop = swiperOutterRef.current.getBoundingClientRect().top;
          swiperRef.current.style = `
            height: 50vh; 
            transform: scale(1, 1.12); 
            transition: all ${settings.mobile_scale_speed / 10}s ease;
          `;
          swiperOutterRef.current.style = `
            transform-origin: top; 
            transform: scale(1.12, 1) translateY(${-offsetTop}px); 
            z-index: 9999999; 
            transition: all ${settings.mobile_scale_speed / 10}s ease;
          `;
        }
      } else {
        document.documentElement.style.removeProperty('overflow-y');
        if (!isDesktop) {
          swiperRef.current.style = '';
          swiperOutterRef.current.style = ``;
        }
      }
    }
  }, [isModalOpen]);

  const slideEls = settings.stl_products.map((stlProduct, stlProductIndex) => (
    stlProduct && (
    <SwiperSlide
      key={stlProductIndex}
      onClick={() => openModal(stlProductIndex)}
    >
      <img
        className={`Image--lazyLoad ${isModalOpen && (stlProduct.fit_featured_image ? 'image-cover' : 'image-contain')}`}
        src={window.resizeImage(stlProduct.featured_image, 1, 1)}
        srcSet={window.resizeImage(
          window.replaceNoImage(stlProduct.featured_image).src,
          768,
        )}
        alt={stlProduct.featured_image_alt || 'Shop The Look Image'}
      />
    </SwiperSlide>
    )
  ));

  const mobileAutoplaySpeed = 10000 * +settings.mobile_autoplay_speed / 100;

  return (
    <div className="products-slider-wrapper">
      <div className="text-block">
        <p className="text">{settings.subtitle}</p>
        <h2 className="h3">{settings.title}</h2>
        {window.shopTheLookSettings.showCTA && (
          <a
            className={`${window.shopTheLookSettings.colorCTA} Button stlmargin`}
            href={window.shopTheLookSettings.linkCTA}
          >
            {window.shopTheLookSettings.textCTA}
          </a>
        )}
      </div>
      {!isDesktop ? (
        <div
          ref={swiperOutterRef}
          className={`${isModalOpen ? 'active-slide--mobile ' : ''}products-slider`}
        >
          <Swiper
            className="stl__swiper"
            ref={swiperRef}
            allowTouchMove
            loop
            spaceBetween={isModalOpen ? 0 : 16}
            slidesPerView={1}
            speed={750}
            autoplay={{
              delay: mobileAutoplaySpeed,
              disableOnInteraction: true,
            }}
            navigation={settings.mobile_slider_btns && {
              prevEl: '.stl__btn--prev',
              nextEl: '.stl__btn--next',
              disabledClass: '.stl__btn--disabled',
            }}
            onSlideChange={(swiper) => setCurrentStlProductIdx(swiper.realIndex)}
          >
            {settings.mobile_slider_btns && (
              <>
                <button className="stl__btn stl__btn--prev">
                  <BsChevronLeft className="stl__icon" />
                </button>
                <button className="stl__btn stl__btn--next">
                  <BsChevronRight className="stl__icon" />
                </button>
              </>
            )}
            {slideEls}
          </Swiper>
        </div>
      ) : (
        <div
          onMouseOver={pause}
          onDragCapture={pause}
          onMouseLeave={() => {
            if (isModalOpen) return;
            play();
          }}
          className="products-slider"
        >
          <Flickity
            ref={flickityRef}
            elementType="div" // default 'div'
            options={flickityOptions} // takes flickity options {}
            disableImagesLoaded
            reloadOnUpdate
          >
            {[...settings.stl_products, ...settings.stl_products].map(
              (stlProduct, stlProductIndex) => (
                stlProduct && (
                <div
                  key={stlProductIndex}
                  className="slider-image__wrapper"
                >
                  <div className="slider-image__container">
                    <img
                      className="Image--lazyLoad"
                      onClick={() => {
                        if (!isMouseMoved.current) {
                          const idx = stlProductIndex >= settings.stl_products.length
                            ? stlProductIndex
                                    - settings.stl_products.length
                            : stlProductIndex;

                          openModal(idx);
                        }
                        isMouseMoved.current = false;
                      }}
                      data-srcset={matchImageToMedia(DESKTOP_IMAGE_MEDIA, stlProduct.featured_image)}
                      src={window.resizeImage(stlProduct.featured_image, 768)}
                      alt="Shop the Look Image"
                    />
                  </div>
                </div>
                )
              ),
            )}
          </Flickity>
        </div>
      )}
      {isDesktop ? (
        isModalOpen && (
          <ModalFullScreenAsync
            settings={settings}
            closeModal={closeModal}
            currentProduct={[currentStlProductIdx, setCurrentStlProductIdx]}
          />
        )
      ) : (
        <ProductsSliderModalMobileAsync
          isOpen={isModalOpen}
          products={settings.stl_products?.[currentStlProductIdx]?.products}
          currentProductSlideIdx={currentProductSlideIdx}
          setActiveHotspot={(index) => setActiveHotspot(index)}
          closeModal={closeModal}
        />
      )}
    </div>
  );
}

export default memo(SliderSection);
